// Sprint-16 — bell-anchored notification dropdown.
//
// Layout:
//   ┌────────────────────────── 320px ──────────────────────────┐
//   │ Notifications              Mark all read                  │
//   ├───────────────────────────────────────────────────────────┤
//   │ • [icon] Title                                  2h        │
//   │         Body excerpt clamped to two lines.                │
//   │   [icon] Older title                            yesterday │
//   │         …                                                 │
//   │   (scroll — max-height 420px)                             │
//   ├───────────────────────────────────────────────────────────┤
//   │ Open inbox →                                              │
//   └───────────────────────────────────────────────────────────┘
//
// Positioning: rendered INSIDE the bell wrapper as an absolutely positioned
// element with `right: 0; top: 100% + 8px`. No portal — the dropdown lives
// in the same DOM subtree as the bell so click-outside detection is one
// `contains` check on a single ref. The trade-off is that the dropdown
// CAN clip if the bell is too close to an `overflow: hidden` container;
// AppShell's header doesn't clip, so this is safe for our shell.
//
// Why not a popover lib? The whole header (theme switcher, etc.) hand-rolls
// the same pattern — adding @radix-ui/react-popover or floating-ui for ONE
// dropdown is a 30kb dependency for a 60-line component.

import { useTranslation } from 'react-i18next';
import { useNotifications } from '../../stores/notificationsStore';
import { NotificationItem } from './NotificationItem';
import { Link } from 'react-router-dom';

interface Props {
  /** Closes the dropdown — wired to outside-click + item-click + Esc in the parent. */
  onClose: () => void;
}

export function InboxDropdown({ onClose }: Props) {
  const { t } = useTranslation(['console', 'common']);
  // Individual selectors only — project rule. See store header.
  const items       = useNotifications(s => s.items);
  const hydrated    = useNotifications(s => s.hydrated);
  const markAllRead = useNotifications(s => s.markAllRead);
  const unread      = useNotifications(s => s.unreadCount);

  // Only show the latest 8 in the dropdown — the "Open inbox" footer link
  // takes the user to the full page for history. 8 is the visual sweet
  // spot for a 420px max-height scroll region with two-line bodies.
  const visible = items.slice(0, 8);

  return (
    <div
      role="menu"
      aria-label={t('inboxDropdown.notifications')}
      className="absolute right-0 top-full mt-2 w-[320px] rounded-md ws-anim-fade-up z-50"
      style={{
        background: 'var(--ws-surface)',
        border: '1px solid var(--ws-border)',
        boxShadow: 'var(--ws-shadow-lg)',
        overflow: 'hidden',
      }}>
      {/* Header — title + mark-all-read. The "mark all" link is hidden when
          there's nothing to mark so it doesn't visually offer a dead action. */}
      <div
        className="flex items-center justify-between px-3 py-2.5"
        style={{ borderBottom: '1px solid var(--ws-border)' }}>
        <p className="ws-eyebrow">{t('inboxDropdown.notifications')}</p>
        {unread > 0 && (
          <button
            type="button"
            onClick={() => markAllRead()}
            className="text-[11px]"
            style={{
              color: 'var(--ws-accent-ink)',
              textDecoration: 'underline',
              textUnderlineOffset: 2,
            }}>
            {t('inbox.markAllRead')}
          </button>
        )}
      </div>

      {/* Scrollable body. max-h gives ~6-7 rows visible at compact sizes, then
          scrolls. The list itself is a div (not <ul>) — NotificationItem is a
          button so semantics are clear without nested interactive roles. */}
      <div className="overflow-y-auto" style={{ maxHeight: 420 }}>
        {!hydrated && (
          <div className="px-3 py-5 text-center text-[12px]"
               style={{ color: 'var(--ws-ink-3)' }}>
            {t('common:status.loading')}
          </div>
        )}
        {hydrated && visible.length === 0 && (
          <div className="px-3 py-6 text-center">
            <p className="ws-eyebrow">{t('inboxDropdown.allCaughtUp')}</p>
            <p className="text-[12px] mt-1.5" style={{ color: 'var(--ws-ink-3)' }}>
              {t('inboxDropdown.nothingNew')}
            </p>
          </div>
        )}
        {hydrated && visible.length > 0 && (
          <div className="p-1.5 flex flex-col gap-0.5">
            {visible.map(n => (
              <NotificationItem
                key={n.id}
                notification={n}
                onClick={onClose}
              />
            ))}
          </div>
        )}
      </div>

      {/* Footer — direct link to the full /inbox page. Always present so the
          dropdown is a peek and the full history is one click away. */}
      <div style={{ borderTop: '1px solid var(--ws-border)' }}>
        <Link
          to="/inbox"
          onClick={onClose}
          className="block px-3 py-2.5 text-[12px] text-center"
          style={{ color: 'var(--ws-ink-2)' }}
          onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--ws-ink)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--ws-ink-2)'; }}>
          {t('inboxDropdown.openInbox')}
        </Link>
      </div>
    </div>
  );
}
